/**
 * Iter 253v · Sentiment Trends Dashboard
 *
 * Managers see per-client weekly mood curves and an "at-risk" callout of
 * clients whose satisfaction is slipping — the moment to schedule a check-in
 * before a churn conversation.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { AlertTriangle, TrendingDown, TrendingUp, ChevronRight, RefreshCw, Info } from "lucide-react";

const MOOD_COLORS = {
  delight: "#059669",
  positive: "#22c55e",
  neutral: "#64748b",
  concern: "#eab308",
  disappointment: "#dc2626",
};

function scoreColor(score) {
  if (score === null || score === undefined) return "#e2e8f0";
  if (score >= 0.6) return MOOD_COLORS.delight;
  if (score >= 0.2) return MOOD_COLORS.positive;
  if (score >= -0.2) return MOOD_COLORS.neutral;
  if (score >= -0.6) return MOOD_COLORS.concern;
  return MOOD_COLORS.disappointment;
}

function Sparkline({ weeks, height = 32, width = 200 }) {
  // Simple SVG polyline. Nulls -> break the line.
  const scored = weeks.filter((w) => w.avg_score !== null && w.avg_score !== undefined);
  if (scored.length < 2) {
    return <div className="text-[10px] text-muted-foreground italic">not enough data</div>;
  }
  const pts = weeks.map((w, i) => {
    if (w.avg_score === null || w.avg_score === undefined) return null;
    const x = (i / (weeks.length - 1)) * width;
    const y = height - ((w.avg_score + 1) / 2) * height;
    return { x, y, score: w.avg_score };
  });
  const path = [];
  let started = false;
  pts.forEach((p) => {
    if (!p) { started = false; return; }
    path.push(`${started ? "L" : "M"}${p.x},${p.y}`);
    started = true;
  });
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
      <line x1="0" y1={height / 2} x2={width} y2={height / 2}
              stroke="#e2e8f0" strokeDasharray="2 3" strokeWidth="1" />
      <path d={path.join(" ")} fill="none" stroke="#0f172a" strokeWidth="1.5" />
      {pts.filter(Boolean).map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="3" fill={scoreColor(p.score)} stroke="white" strokeWidth="1" />
      ))}
    </svg>
  );
}

function WorkspaceStackedBars({ weeks, height = 40 }) {
  // Stacked bar per week showing mood distribution
  const max = Math.max(1, ...weeks.map((w) => w.submission_count || 0));
  const width = weeks.length * 32;
  return (
    <div className="overflow-x-auto">
      <svg width={width} height={height + 18} viewBox={`0 0 ${width} ${height + 18}`}>
        {weeks.map((w, i) => {
          const total = w.submission_count || 0;
          const barH = (total / max) * height;
          const x = i * 32 + 8;
          let y = height - barH;
          const bars = [];
          ["delight", "positive", "neutral", "concern", "disappointment"].forEach((m) => {
            const cnt = w.mood_counts[m] || 0;
            if (cnt && total > 0) {
              const h = (cnt / total) * barH;
              bars.push(<rect key={m} x={x} y={y} width={16} height={h} fill={MOOD_COLORS[m]} />);
              y += h;
            }
          });
          return (
            <g key={w.week_start}>
              {total > 0 ? bars : <rect x={x} y={height - 2} width={16} height={2} fill="#e2e8f0" />}
              <text x={x + 8} y={height + 12} textAnchor="middle"
                     fontSize="9" fill="#64748b">
                {w.week_start.slice(5)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export default function SentimentTrends() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [weeks, setWeeks] = useState(8);
  const [data, setData] = useState(null);
  const [expandedClient, setExpandedClient] = useState(null);

  const load = async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const r = await api.get(`/workspaces/${wsId}/feedback/sentiment-trends`,
                                    { params: { weeks } });
      setData(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load trends")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, weeks]);

  const summary = useMemo(() => {
    if (!data) return null;
    const withScores = data.weeks.filter((w) => w.avg_score !== null);
    const avg = withScores.length ? withScores.reduce((a, w) => a + w.avg_score, 0) / withScores.length : null;
    const total = data.weeks.reduce((a, w) => a + (w.submission_count || 0), 0);
    return { avg, total };
  }, [data]);

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="sentiment-trends-page">
      <PageHeader
        title="Sentiment Trends"
        subtitle="Weekly mood curve per client — spot slipping satisfaction before churn." />
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-xs uppercase tracking-widest text-muted-foreground" htmlFor="weeks-input">Window:</label>
        <select id="weeks-input" value={weeks} className="border border-border bg-background h-8 px-2 text-sm rounded-none"
                onChange={(e) => setWeeks(Number(e.target.value))}
                data-testid="sentiment-trends-weeks">
          <option value={4}>4 weeks</option>
          <option value={8}>8 weeks</option>
          <option value={13}>13 weeks (quarter)</option>
          <option value={26}>26 weeks</option>
        </select>
        <Button variant="outline" size="sm" onClick={load} className="rounded-none h-8" data-testid="sentiment-trends-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
        {summary && (
          <span className="ml-auto text-xs text-muted-foreground">
            {summary.total} scored comment{summary.total === 1 ? "" : "s"}
            {summary.avg !== null && (
              <> · avg <strong style={{ color: scoreColor(summary.avg) }}>{summary.avg.toFixed(2)}</strong></>
            )}
          </span>
        )}
      </div>

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}

      {data?.at_risk?.length > 0 && (
        <div className="border-l-4 border-rose-500 bg-rose-50 px-4 py-3" data-testid="sentiment-at-risk-banner">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-600 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-rose-800">
                {data.at_risk.length} client{data.at_risk.length === 1 ? "" : "s"} slipping —
                schedule a check-in before this becomes churn.
              </div>
              <div className="mt-1 text-xs text-rose-700 space-y-1">
                {data.at_risk.slice(0, 5).map((c) => (
                  <div key={c.client_email || c.client_name}
                       className="flex items-center gap-2" data-testid={`sentiment-at-risk-${(c.client_email || "unknown").replace(/[^a-zA-Z0-9]/g, "-")}`}>
                    <TrendingDown className="w-3 h-3" />
                    <span className="font-medium">{c.client_name || c.client_email}</span>
                    <span className="text-rose-500">avg {c.overall_avg?.toFixed(2)}</span>
                    <span className="text-rose-500">Δ {c.trend?.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {data && (
        <div className="border border-border bg-background p-4 space-y-2" data-testid="sentiment-workspace-chart">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Workspace mood distribution · last {weeks} weeks</div>
          <WorkspaceStackedBars weeks={data.weeks} />
          <div className="flex gap-3 text-[10px] mt-1 flex-wrap">
            {Object.entries(MOOD_COLORS).map(([mood, color]) => (
              <span key={mood} className="inline-flex items-center gap-1 uppercase tracking-wider">
                <span className="w-2 h-2 inline-block" style={{ background: color }} />
                {mood}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="border border-border" data-testid="sentiment-clients-table">
        <table className="w-full text-xs">
          <thead className="bg-muted/40">
            <tr>
              <th className="text-left px-3 py-2 uppercase text-[10px] tracking-widest font-semibold">Client</th>
              <th className="text-left px-3 py-2 uppercase text-[10px] tracking-widest font-semibold">Trend</th>
              <th className="text-left px-3 py-2 uppercase text-[10px] tracking-widest font-semibold">Recent avg</th>
              <th className="text-left px-3 py-2 uppercase text-[10px] tracking-widest font-semibold">Overall avg</th>
              <th className="text-left px-3 py-2 uppercase text-[10px] tracking-widest font-semibold">Comments</th>
              <th className="text-left px-3 py-2 uppercase text-[10px] tracking-widest font-semibold">Sparkline</th>
            </tr>
          </thead>
          <tbody>
            {!data?.clients?.length && (
              <tr><td colSpan="6" className="p-6 text-center text-muted-foreground" data-testid="sentiment-clients-empty">
                No feedback with sentiment yet. Comments get scored automatically as new submissions land.
              </td></tr>
            )}
            {data?.clients?.map((c) => {
              const rowId = (c.client_email || c.client_name || "row").replace(/[^a-zA-Z0-9]/g, "-");
              const trendIcon = c.trend > 0 ? <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
                : c.trend < 0 ? <TrendingDown className="w-3.5 h-3.5 text-rose-600" />
                : <span className="text-muted-foreground">–</span>;
              return (
                <React.Fragment key={rowId}>
                  <tr className={`border-t border-border ${c.slipping ? "bg-rose-50/60" : ""}`}
                      data-testid={`sentiment-client-row-${rowId}`}>
                    <td className="px-3 py-2">
                      <button type="button" className="flex items-center gap-1 font-medium hover:underline text-left"
                              onClick={() => setExpandedClient(expandedClient === rowId ? null : rowId)}>
                        <ChevronRight className={`w-3 h-3 transition-transform ${expandedClient === rowId ? "rotate-90" : ""}`} />
                        {c.client_name || c.client_email || "Unknown"}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1">{trendIcon}{c.trend?.toFixed(2)}</span>
                    </td>
                    <td className="px-3 py-2" style={{ color: scoreColor(c.recent_avg) }}>
                      {c.recent_avg?.toFixed(2) || "–"}
                    </td>
                    <td className="px-3 py-2" style={{ color: scoreColor(c.overall_avg) }}>
                      {c.overall_avg?.toFixed(2) || "–"}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{c.submission_count}</td>
                    <td className="px-3 py-2"><Sparkline weeks={c.weeks} /></td>
                  </tr>
                  {expandedClient === rowId && (
                    <tr className="bg-muted/20">
                      <td colSpan="6" className="px-3 py-2">
                        <div className="flex gap-1 flex-wrap">
                          {c.weeks.map((w) => (
                            <div key={w.week_start} className="text-[10px] border border-border p-1.5 min-w-[80px]">
                              <div className="text-muted-foreground">{w.week_start}</div>
                              <div style={{ color: scoreColor(w.avg_score) }} className="font-semibold">
                                {w.avg_score !== null && w.avg_score !== undefined ? w.avg_score.toFixed(2) : "–"}
                              </div>
                              <div className="text-muted-foreground">{w.count} rating{w.count === 1 ? "" : "s"}</div>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-muted-foreground inline-flex items-start gap-1">
        <Info className="w-3 h-3 mt-0.5" />
        Sentiment is inferred from client comments by Claude Sonnet 4.5.
        Scores range from -1 (disappointment) to +1 (delight). Clients marked
        "slipping" have a recent-2-week average below zero with a negative trend.
      </div>
    </div>
  );
}
